// ─────────────────────────────────────────────
//  useIsMobile — track whether the viewport is below a breakpoint
//  Drives the responsive contract (single-column cards, scrollable stats,
//  hidden sidebar) without Tailwind. Guarded for non-browser/test envs.
// ─────────────────────────────────────────────
import { useEffect, useState } from "react";

export const MOBILE_BREAKPOINT = 768;

// The width below which a multi-column page layout must collapse to one column.
//
// ⚠️ THIS IS NOT `MOBILE_BREAKPOINT`, AND CONFLATING THEM IS A REAL BUG.
// `isMobile` answers "is this a phone" (768). A side-by-side column layout needs
// a great deal more room than that, and the 768–1023 band is where the two
// questions give different answers.
//
// Measured on /timeline before this existed: the 7-column grid still applied at
// 834px, which handed the status rail a **92px** track. Its content overflowed
// by 20px (`scrollWidth` 112 vs `clientWidth` 92), so alarm-coloured text painted
// over the Gantt chart, and the fixed-height alarm rows wrapped to uneven
// heights — the exact reflow those rows exist to prevent.
// 1280, derived from the geometry rather than picked from a device list.
//
// In a 7-track grid, column 1 is `(W - 44px page padding - 144px of gaps) / 7`.
// The status rail needs ~112px before its content overflows and ~179px to look
// as designed. That gives:
//     834px  ->  92px  (overflowed by 20px, alarm colours painted over the chart)
//    1024px  -> 119px  (fits, but rows wrap and lose their fixed height)
//    1280px  -> 156px  (comfortable)
//    1440px  -> 179px  (as designed)
// So the split may not start below ~1280. Choosing 1024 because it is a
// familiar tablet number would put the rail 37px under its own minimum.
export const SPLIT_LAYOUT_BREAKPOINT = 1280;

// True when the viewport is too narrow to place columns side by side, so the
// page should stack. Deliberately a separate hook from `useIsMobile` so a caller
// has to choose which question it is asking.
export function useIsStacked(breakpoint = SPLIT_LAYOUT_BREAKPOINT) {
  return useIsMobile(breakpoint);
}

export function useIsMobile(breakpoint = MOBILE_BREAKPOINT) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}
